import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import type {
  Goal,
  GoalActivityInitiative,
  GoalActivityProject,
  GoalActivityTask,
  GoalState,
} from '@localagent/sdk';
import { cn } from '@localagent/ui/lib/utils';
import { STATE_COLORS, TICKET_STATE_LABELS } from '../../lib/work-management-helpers';
import {
  BLOCKERS_UNAVAILABLE,
  VERIFICATION_LABELS,
  assigneeResolver,
  blockerIndex,
  nodeLabelIndex,
  type BlockerList,
} from '../../lib/project-board-helpers';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { useGoalActivity, useGoals, useWorkOrderings } from '../../hooks/useWorkManagement';
import { usePositions } from '../../hooks/useOrgChart';
import { setWorkItemSessionsAction, type WorkItemSessionLink } from '../../stores/work/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const GOAL_STATE_LABELS: Record<GoalState, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  decomposing: 'Decomposing',
  executing: 'Executing',
  verifying: 'Verifying',
  completed: 'Completed',
  paused: 'Paused',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const GOAL_STATE_COLORS: Record<GoalState, string> = {
  draft: 'bg-muted text-foreground-secondary',
  scheduled: 'bg-chart-5/15 text-chart-5',
  decomposing: 'bg-chart-1/15 text-chart-1',
  executing: 'bg-info-subtle text-info-text',
  verifying: 'bg-chart-6/15 text-chart-6',
  completed: 'bg-success-subtle text-success-text',
  paused: 'bg-muted text-foreground-secondary',
  cancelled: 'bg-error-subtle text-error-text',
  failed: 'bg-error-subtle text-error-text',
};

const NODE_STATE_COLORS: Record<string, string> = {
  planned: 'bg-muted text-foreground-secondary',
  active: 'bg-info-subtle text-info-text',
  paused: 'bg-muted text-foreground-secondary',
  completed: 'bg-success-subtle text-success-text',
  cancelled: 'bg-error-subtle text-error-text',
};

const chipClasses = 'inline-flex items-center rounded px-1.5 py-0.5 text-micro font-medium';

const linkChipClasses =
  'inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-micro font-medium text-primary transition-colors hover:bg-primary/20';

function nodeStateChip(state: string) {
  return cn(chipClasses, NODE_STATE_COLORS[state] ?? 'bg-muted text-foreground-secondary');
}

function SessionLink({ sessionId, label, testId }: { sessionId: string; label: string; testId: string }) {
  return (
    <Link
      to={`/chat/${sessionId}`}
      title="Open the session doing this work"
      data-testid={testId}
      data-session-id={sessionId}
      className={linkChipClasses}
    >
      {label}
    </Link>
  );
}

function BlockedBy({ blockers }: { blockers: BlockerList }) {
  if (blockers === BLOCKERS_UNAVAILABLE) {
    return (
      <p className="text-micro text-muted-foreground" data-testid="work-item-blockers-unavailable">
        Blockers unavailable
      </p>
    );
  }
  if (!blockers || blockers.length === 0) return null;
  return (
    <p className="text-micro text-muted-foreground" data-testid="work-item-blocked-by">
      Blocked by {blockers.join(', ')}
    </p>
  );
}

function TaskRow({
  task,
  assignee,
}: {
  task: GoalActivityTask;
  assignee: string;
}) {
  return (
    <div className="flex items-start justify-between gap-2 py-0.5" data-testid={`goal-activity-task-${task.id}`}>
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <code className="shrink-0 text-micro text-muted-foreground">{task.identifier}</code>
        <span className="truncate text-micro" title={task.title}>
          {task.title}
        </span>
        <span className={cn(chipClasses, STATE_COLORS[task.state])}>
          {TICKET_STATE_LABELS[task.state]}
        </span>
        <span className="text-micro text-muted-foreground">{assignee}</span>
      </span>
      {task.session_id && (
        <SessionLink
          sessionId={task.session_id}
          label="Session"
          testId={`goal-activity-task-session-${task.id}`}
        />
      )}
    </div>
  );
}

function ProjectNode({
  project,
  assignee,
  resolveActor,
  blockers,
}: {
  project: GoalActivityProject;
  assignee: string;
  resolveActor: (personaId?: string | null, userId?: string | null) => string;
  blockers: BlockerList;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div data-testid={`goal-activity-project-${project.id}`} data-task-count={project.tasks.length}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 py-0.5 text-left hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="inline-block w-2 shrink-0 text-center text-micro text-muted-foreground">
            {open ? '▾' : '▸'}
          </span>
          <span className="truncate text-caption font-medium">{project.name}</span>
          <span className={nodeStateChip(project.state)}>{project.state}</span>
          <span className="text-micro text-muted-foreground">{assignee}</span>
          <span className="text-micro text-muted-foreground">
            {VERIFICATION_LABELS[project.verification_state]}
          </span>
        </span>
      </button>

      <div className="ml-4 space-y-0.5">
        <BlockedBy blockers={blockers} />
        {project.session_id && (
          <SessionLink
            sessionId={project.session_id}
            label="Project session"
            testId={`goal-activity-project-session-${project.id}`}
          />
        )}
        {open &&
          (project.tasks.length === 0 ? (
            <p className="text-micro text-muted-foreground">No tasks yet.</p>
          ) : (
            project.tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                assignee={resolveActor(task.assignee_persona_id, task.assignee_user_id)}
              />
            ))
          ))}
      </div>
    </div>
  );
}

function InitiativeNode({
  initiative,
  owner,
  resolvePosition,
  resolveActor,
  blockersFor,
}: {
  initiative: GoalActivityInitiative;
  owner: string;
  resolvePosition: (id?: string | null) => string;
  resolveActor: (personaId?: string | null, userId?: string | null) => string;
  blockersFor: (nodeId: string) => BlockerList;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div
      data-testid={`goal-activity-initiative-${initiative.id}`}
      data-project-count={initiative.projects.length}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 py-0.5 text-left hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="inline-block w-2 shrink-0 text-center text-micro text-muted-foreground">
            {open ? '▾' : '▸'}
          </span>
          <span className="truncate text-caption font-semibold">{initiative.name}</span>
          <span className={nodeStateChip(initiative.state)}>{initiative.state}</span>
          <span className="text-micro text-muted-foreground">{owner}</span>
        </span>
      </button>

      <div className="ml-4 space-y-0.5">
        <BlockedBy blockers={blockersFor(initiative.id)} />
        {open &&
          (initiative.projects.length === 0 ? (
            <p className="text-micro text-muted-foreground">No projects yet.</p>
          ) : (
            initiative.projects.map((project) => (
              <ProjectNode
                key={project.id}
                project={project}
                assignee={resolvePosition(project.assignee_position_id)}
                resolveActor={resolveActor}
                blockers={blockersFor(project.id)}
              />
            ))
          ))}
      </div>
    </div>
  );
}

function GoalRow({
  goal,
  selected,
  onSelect,
}: {
  goal: Goal;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`goal-row-${goal.id}`}
      data-selected={selected}
      className={cn(
        'flex w-full items-start justify-between gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/50',
        selected && 'bg-accent'
      )}
    >
      <span className="min-w-0 truncate text-caption" title={goal.objective}>
        {goal.objective}
      </span>
      <span className={cn(chipClasses, 'shrink-0', GOAL_STATE_COLORS[goal.state])}>
        {GOAL_STATE_LABELS[goal.state]}
      </span>
    </button>
  );
}

interface GoalActivityPanelProps {
  teamId: string;
  /** Read from the route by the caller — the panel owns no goal selection state (app-shell ADR-010). */
  goalId: string | null;
  onSelectGoal: (goalId: string) => void;
}

export function GoalActivityPanel({ teamId, goalId, onSelectGoal }: GoalActivityPanelProps) {
  const setWorkItemSessions = useSetAtom(setWorkItemSessionsAction);

  const { data: goals, isLoading: goalsLoading, error: goalsQueryError } = useGoals(teamId);
  const goalsError = errorWithNothingCachedToShow(goals, goalsQueryError);
  const selectedGoalId =
    goalId && goals?.some((g) => g.id === goalId) ? goalId : goals?.[0]?.id ?? null;

  const { data: activity, isLoading: activityLoading, error: activityQueryError } =
    useGoalActivity(selectedGoalId);
  const activityError = errorWithNothingCachedToShow(activity, activityQueryError);
  const { data: orderings, error: orderingsQueryError } = useWorkOrderings(selectedGoalId);
  const orderingsError = errorWithNothingCachedToShow(orderings, orderingsQueryError);
  const { data: positions, error: positionsQueryError } = usePositions();
  const positionsError = errorWithNothingCachedToShow(positions, positionsQueryError);

  const resolve = useMemo(() => assigneeResolver(positions), [positions]);

  const nodeLabels = useMemo(() => nodeLabelIndex(activity?.initiatives), [activity]);

  const blockers = useMemo(() => blockerIndex(orderings, nodeLabels), [orderings, nodeLabels]);
  const blockersFor = useCallback(
    (nodeId: string): BlockerList =>
      orderingsError ? BLOCKERS_UNAVAILABLE : blockers.get(nodeId),
    [orderingsError, blockers],
  );

  const sessionLinks = useMemo(() => {
    const links: Record<string, WorkItemSessionLink> = {};
    for (const initiative of activity?.initiatives ?? []) {
      for (const project of initiative.projects) {
        if (project.session_id) {
          links[project.id] = { sessionId: project.session_id, running: project.state === 'active' };
        }
        for (const task of project.tasks) {
          if (task.session_id) {
            links[task.id] = { sessionId: task.session_id, running: task.state === 'in_progress' };
          }
        }
      }
    }
    return links;
  }, [activity]);

  useEffect(() => {
    setWorkItemSessions(sessionLinks);
  }, [sessionLinks, setWorkItemSessions]);

  const managerSessionId = activity?.goal.manager_session_id;

  return (
    <div className="flex h-full w-96 flex-shrink-0 flex-col border-l bg-background" data-testid="goal-activity-panel">
      <div className="border-b px-4 py-3">
        <h2 className="text-label font-semibold">Goals</h2>
      </div>

      <div className="max-h-48 space-y-1 overflow-auto border-b p-3">
        {goalsLoading && <LoadingState variant="inline" label="Loading goals..." />}
        {goalsError && (
          <p className="text-caption text-destructive">Failed to load goals: {goalsError.message}</p>
        )}
        {!goalsLoading && !goalsError && (goals?.length ?? 0) === 0 && (
          <p className="text-caption text-muted-foreground" data-testid="goal-activity-empty">
            No goals for this team yet.
          </p>
        )}
        {goals?.map((goal) => (
          <GoalRow
            key={goal.id}
            goal={goal}
            selected={goal.id === selectedGoalId}
            onSelect={() => onSelectGoal(goal.id)}
          />
        ))}
      </div>

      <div className="flex-1 space-y-1 overflow-auto p-3">
        {selectedGoalId && activityLoading && (
          <LoadingState variant="inline" label="Loading activity..." />
        )}
        {activityError && (
          <p className="text-caption text-destructive">
            Failed to load activity: {activityError.message}
          </p>
        )}
        {orderingsError && (
          <p className="text-caption text-destructive" data-testid="goal-activity-blockers-error">
            Blockers unavailable: {orderingsError.message}
          </p>
        )}
        {positionsError && (
          <p className="text-caption text-destructive" data-testid="goal-activity-assignees-error">
            Assignees unavailable: {positionsError.message}
          </p>
        )}

        {managerSessionId && (
          <SessionLink
            sessionId={managerSessionId}
            label="Open goal session"
            testId="goal-manager-session-link"
          />
        )}

        {activity && activity.initiatives.length === 0 && (
          <p className="text-caption text-muted-foreground" data-testid="goal-activity-no-initiatives">
            Nothing beneath this goal yet.
          </p>
        )}

        {activity?.initiatives.map((initiative) => (
          <InitiativeNode
            key={initiative.id}
            initiative={initiative}
            owner={resolve.position(initiative.owner_position_id)}
            resolvePosition={resolve.position}
            resolveActor={resolve.actor}
            blockersFor={blockersFor}
          />
        ))}
      </div>
    </div>
  );
}
